/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import { Validators } from '@angular/forms';
import {
    type ActionCloseArgs, type Command, type Rest, CAPACITY_UNIT, DMValidators, FormControl, FormGroup,
    FormSlideWindowAbstract, UNPLACEHOLDER, UtilService,
} from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { MO } from 'app/common/constants';
import { ToolService } from 'app/common/services';
import { type Dtree, type FileSystem, type Quota } from 'app/common/types/mo';
import { ProductConfig } from 'app/config';
import { type Observable, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { SelectUserOrGroupComponent } from '../../select-user-or-group/select-user-or-group.component';
import { type QuotaVm, type SelectTypeE, type UserOrGroup, NameTypeE, QuotaTypeE, UserOrGroupTypeE } from '../../user-quota';
import { UserQuotaService } from '../../user-quota.service';
import { UserQuotaStrService } from '../../user-quota-str.service';

@Component({
    selector: 'app-common-create-user-quota',
    templateUrl: './create-front-table-user-quota.component.html',
    styleUrls: ['./create-front-table-user-quota.component.scss'],
})
export class CreateFrontTableUserQuotaComponent extends FormSlideWindowAbstract implements OnInit {
    @Input() fileSystem: FileSystem;
    @Input() dtree?: Dtree;
    @Input() vstoreId: string;
    @Input() vstoreName: string;

    i18n = this.str.i18n;
    QuotaTypeE = QuotaTypeE;
    UNPLACEHOLDER = UNPLACEHOLDER;
    ProductConfig = ProductConfig;

    vm: FormGroup;
    errorMessage: any;
    /**
     * 用户必填校验
     */
    userRequiredErr = false;
    /**
     * 用户组必填校验
     */
    userGroupRequiredErr = false;

    /**
     * 已选用户
     * undefined表示没选，
     * null表示所有用户
     * 其他情况表示选中数据
     */
    users: UserOrGroup[];
    /**
     * 已选用户描述
     */
    usersDesc: string;
    /**
     * 用户已选类型
     */
    userSelectType: SelectTypeE;
    /**
     * 已选用户组
     * undefined表示没选，
     * null表示所有用户
     * 其他情况表示选中数据
     */
    userGroups: UserOrGroup[];
    /**
     * 已选用户描述
     */
    userGroupsDesc: string;
    /**
     * 用户组已选类型
     */
    userGroupSelectType: SelectTypeE;
    canFsChange = true;
    canDtreeChange = true;
    showDtree = true;
    createFsDisabled = false;

    fileSystemRest: Rest;
    dtreeRest: Rest;
    selectedDtree: Dtree;

    allDtreeOption: SelectOption = {
        label: this.i18n.allDtreeLab,
        value: null,
        data: null,
    };

    get vmValue(): QuotaVm {
        return this.vm ? this.vm.value : null;
    }

    constructor(
        public str: UserQuotaStrService,
        private service: UserQuotaService,
        injector: Injector,
        private toolService: ToolService,
        private utilService: UtilService,
    ) {
        super(injector);
    }


    ngOnInit(): void {
        super.ngOnInit();

        this.entryHander();

        this.pageReady = true;
        this.windowVisible = true;
    }

    /**
     * 配额入口判断和处理
     */
    entryHander(): void {
        if (this.fileSystem) {
            // dtree
            if (this.dtree) {
                this.showDtree = true;
            } else {
                // 文件系统
                this.showDtree = false;
            }
        }
    }

    initVm(): void {
        const spaceQuotaValidator = DMValidators.capacityValidator(false, this.str.spaceQuotaMin, this.str.spaceQuotaMax);
        this.vm = new FormGroup({
            quotaType: new FormControl(ProductConfig.isHideDtreeQuota ? QuotaTypeE.User : QuotaTypeE.Dtree),
            spaceQuota: new FormGroup({
                hard: new FormControl({ value: '', unit: 'MB' }, {
                    validators: [
                        spaceQuotaValidator,
                    ],
                }),
                soft: new FormControl({ value: '', unit: 'MB' }, {
                    validators: [
                        spaceQuotaValidator,
                    ],
                }),
            }, { validators: [this.service.spaceCompareValidator] }),
            fileQuota: new FormGroup({
                hard: new FormControl(null, {
                    validators: [
                        Validators.min(this.str.fileQuotaMin),
                        Validators.max(this.str.fileQuotaMax),
                    ],
                }),
                hardUnit: new FormControl(1000),
                soft: new FormControl(null, {
                    validators: [
                        Validators.min(this.str.fileQuotaMin),
                        Validators.max(this.str.fileQuotaMax),
                    ],
                }),
                softUnit: new FormControl(1000),
            }, { validators: [this.service.fileCompareValidator] }),
        }, {
            updateOn: 'blur',
        });

        const spaceHardQuotaErr = this.toolService.rangeErr(this.i18n.hardQuotaLab, this.str.spaceQuotaMin, this.str.spaceQuotaMax);
        const spaceSoftQuotaErr = this.toolService.rangeErr(this.i18n.softQuotaLab, this.str.spaceQuotaMin, this.str.spaceQuotaMax);
        const fileHardQuotaErr = this.toolService.rangeErr(this.i18n.hardQuotaLab, this.str.fileQuotaMin,
            this.str.billion(this.i18nService.language === 'zh' ? '20' : '2'));
        const fileSoftQuotaErr = this.toolService.rangeErr(this.i18n.softQuotaLab, this.str.fileQuotaMin,
            this.str.billion(this.i18nService.language === 'zh' ? '20' : '2'));

        this.errorMessage = {
            spaceQuota: {
                hard: {
                    min: spaceHardQuotaErr,
                    max: spaceHardQuotaErr,
                },
                soft: {
                    min: spaceSoftQuotaErr,
                    max: spaceSoftQuotaErr,
                    quotaCompare: this.i18n.quotaCompareErr,
                },
            },
            fileQuota: {
                hard: {
                    fileHardQuota: fileHardQuotaErr,
                },
                hardUnit: {
                    fileHardQuota: fileHardQuotaErr,
                },
                soft: {
                    fileSoftQuota: fileSoftQuotaErr,
                    quotaCompare: this.i18n.quotaCompareErr,
                },
                softUnit: {
                    fileSoftQuota: fileSoftQuotaErr,
                },
            },
        };
    }


    /**
     * 配额类型切换
     */
    onQuotaTypeChange(): void {
        this.userRequiredErr = false;
        this.userGroupRequiredErr = false;
    }

    /**
     * 选择用户
     */
    selectUser(): void {
        this.vm.validate();
        if (!this.vm.valid) {
            return;
        }
        this.doAction(SelectUserOrGroupComponent, {
            quotaType: 'user',
            selectType: this.userSelectType,
            vstoreId: this.vstoreId,
            data: this.users,
            onClose: ({ refresh, data, selectType }: ActionCloseArgs) => {
                if (refresh) {
                    this.users = data;
                    this.usersDesc = this.str.getUsersDesc(this.users);
                    this.userSelectType = selectType;
                    this.userRequiredErr = false;
                }
            },
        });
    }

    /**
     * 选择用户组
     */
    selectUserGroup(): void {
        this.vm.validate();
        if (!this.vm.valid) {
            return;
        }
        this.doAction(SelectUserOrGroupComponent, {
            quotaType: 'group',
            selectType: this.userGroupSelectType,
            vstoreId: this.vstoreId,
            data: this.userGroups,
            onClose: ({ refresh, data, selectType }: ActionCloseArgs) => {
                if (refresh) {
                    this.userGroups = data;
                    this.userGroupsDesc = this.str.getUserGroupsDesc(this.userGroups);
                    this.userGroupSelectType = selectType;
                    this.userGroupRequiredErr = false;
                }
            },
        });
    }

    /**
     * 校验
     */
    validate(): Observable<boolean> {
        return forkJoin([this.vm.validateOb(), this.validateUserOrGroup()])
            .pipe(
                // 所有校验都需要通过
                map((results) => results.every((a) => a)),
            );
    }

    /**
     * 校验用户/用户组
     */
    validateUserOrGroup(): Observable<boolean> {
        const vmValue = this.vmValue;

        if (vmValue.quotaType === QuotaTypeE.User && this.users === undefined) {
            this.userRequiredErr = true;
            return of(false);
        }

        if (vmValue.quotaType === QuotaTypeE.UserGroup && this.userGroups === undefined) {
            this.userGroupRequiredErr = true;
            return of(false);
        }

        this.userRequiredErr = false;
        this.userGroupRequiredErr = false;

        return of(true);
    }

    /**
     * 重写方法，创建页不需要修改判定,直接返回true。避免传入了文件系统，直接点确定窗口直接关闭的情况
     */
    isModified(): boolean {
        return true;
    }

    /**
     * 构建创建配额命令
     */
    buildCmds(): Command[] {
        this.closeArgs.data = [];

        const vmValue = this.vmValue;
        const params: Quota = {
            QUOTATYPE: String(vmValue.quotaType),
            SPACEUNITTYPE: '1', // 指定容量单位为KB
        };

        if (vmValue.dtree) {
            params.PARENTTYPE = MO.DTREE;
        } else {
            params.PARENTTYPE = MO.FILE_SYSTEM;
        }

        if (vmValue.spaceQuota.hard.value) {
            params.SPACEHARDQUOTA = String(this.utilService.adaptUnit(
                vmValue.spaceQuota.hard.value,
                vmValue.spaceQuota.hard.unit,
                CAPACITY_UNIT.KB,
            ).value);
        }
        if (vmValue.spaceQuota.soft.value) {
            params.SPACESOFTQUOTA = String(this.utilService.adaptUnit(
                vmValue.spaceQuota.soft.value,
                vmValue.spaceQuota.soft.unit,
                CAPACITY_UNIT.KB,
            ).value);
        }
        if (vmValue.fileQuota.hard) {
            params.FILEHARDQUOTA = String(vmValue.fileQuota.hard * vmValue.fileQuota.hardUnit);
        }
        if (vmValue.fileQuota.soft) {
            params.FILESOFTQUOTA = String(vmValue.fileQuota.soft * vmValue.fileQuota.softUnit);
        }

        if (this.vstoreId) {
            params.vstoreId = this.vstoreId;
        }

        if (vmValue.quotaType === QuotaTypeE.Dtree) {
            this.closeArgs.data.push(params);
        } else {
            const items: UserOrGroup[] = vmValue.quotaType === QuotaTypeE.User ? this.users : this.userGroups;

            if (items) {
                items.forEach((a) => {
                    const newParams: Quota = {
                        ...params,
                        USRGRPTYPE: String(a.USRGRPTYPE),
                        NAMETYPE: String(a.NAMETYPE),
                        USRGRPOWNERNAME: a.USRGRPOWNERNAME,
                        DOMAINTYPE: String(a.DOMAINTYPE),
                        usrOsType: String(a.usrOsType),
                    };
                    this.closeArgs.data.push(newParams);
                });
            } else {
                // 所有用户/用户组
                params.USRGRPOWNERNAME = '*';
                params.USRGRPTYPE = String(UserOrGroupTypeE.Local);
                params.NAMETYPE = vmValue.quotaType === QuotaTypeE.User ?
                    String(NameTypeE.User) :
                    String(NameTypeE.Group);
                this.closeArgs.data.push(params);
            }
        }

        return [];
    }
}
